/**
 * Shared helpers for boss/enemy mechanics (+encounters). Pure functions over the World; all
 * live state is in `world.mechanics` (serializable), all randomness comes from ctx.rng.
 */
import type {
  AffixId,
  Amount,
  EffectCtx,
  EffectSpec,
  HazardIntent,
  KeyScaled,
  MechanicSpec,
  MechanicTargetRule,
  MechanicsState,
  Ms,
  SimContext,
  Telegraph,
  Unit,
  UnitId,
  Vec2,
  World,
  WorldShape,
  Zone,
  ZoneEffect,
  ZoneMotionState,
} from '../../contracts';
import { TICK_MS } from '../../contracts';

/** Ability id used for damage mirrored by a damage link (WCL attribution). */
export const DAMAGE_LINK_ABILITY_ID = 'mechanic.damage_link';
/** Default reach of mechanic target picks (yards). */
export const DEFAULT_PICK_RANGE = 80;
const TWO_PI = 2 * Math.PI;

/** Live mechanic state, created on first use. */
export function mstate(world: World): MechanicsState {
  if (!world.mechanics) {
    world.mechanics = { soaks: [], tethers: [], charges: [], pulls: [], links: [], watched: [], facingLocks: [], intermission: null };
  }
  return world.mechanics;
}

// ---- key level / affixes ------------------------------------------------------

export const keyLevel = (ctx: SimContext): number => ctx.world.mplus?.level ?? 0;
export const hasAffix = (ctx: SimContext, a: AffixId): boolean => (ctx.world.mplus?.affixes ?? []).includes(a);

/** Resolve a KeyScaled value at the current key level and affixes. */
export function resolveKeyScaled(ctx: SimContext, v: KeyScaled | undefined, fallback: number): number {
  if (v === undefined) return fallback;
  if (typeof v === 'number') return v;
  let out = v.base;
  if (v.atKey) {
    const lvl = keyLevel(ctx);
    let best = -Infinity;
    for (const k of Object.keys(v.atKey)) {
      const n = Number(k);
      if (Number.isFinite(n) && n <= lvl && n > best) {
        best = n;
        out = v.atKey[n]!;
      }
    }
  }
  const affixes = ctx.world.mplus?.affixes ?? [];
  for (const a of affixes) {
    const m = v.affixMult?.[a];
    if (m !== undefined) out *= m;
  }
  for (const a of affixes) {
    const add = v.affixAdd?.[a];
    if (add !== undefined) out += add;
  }
  return out;
}

/** Key/affix gate shared by GateMechanic and EnemyAbility gating. */
export function gatePasses(ctx: SimContext, g: { minKey?: number; maxKey?: number; affix?: AffixId; notAffix?: AffixId }): boolean {
  const lvl = keyLevel(ctx);
  if (g.minKey !== undefined && lvl < g.minKey) return false;
  if (g.maxKey !== undefined && lvl > g.maxKey) return false;
  if (g.affix !== undefined && !hasAffix(ctx, g.affix)) return false;
  if (g.notAffix !== undefined && hasAffix(ctx, g.notAffix)) return false;
  return true;
}

// ---- geometry -----------------------------------------------------------------

export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
export const angleTo = (from: Vec2, to: Vec2): number => Math.atan2(to.y - from.y, to.x - from.x);
export function wrapAngle(a: number): number {
  let r = a % TWO_PI;
  if (r > Math.PI) r -= TWO_PI;
  else if (r <= -Math.PI) r += TWO_PI;
  return r;
}
export const polar = (c: Vec2, angle: number, d: number): Vec2 => ({ x: c.x + Math.cos(angle) * d, y: c.y + Math.sin(angle) * d });

// ---- units --------------------------------------------------------------------

export const unitOf = (ctx: SimContext, id: UnitId | null | undefined): Unit | undefined => (id ? ctx.world.units[id] : undefined);

export function hasAuraOn(u: Unit, auraId: string): boolean {
  for (const a of u.auras) if (a.auraId === auraId) return true;
  return false;
}

/** Displacement immunity (aura flag `immuneDisplacement`). */
export function immuneToDisplacement(ctx: SimContext, u: Unit): boolean {
  for (const a of u.auras) if (ctx.content.aura(a.auraId)?.flags?.immuneDisplacement) return true;
  return false;
}

/** Living units hostile to `src`, in deterministic unitOrder, optionally within `range` of it. */
export function hostilesOf(ctx: SimContext, src: Unit, range?: number): Unit[] {
  const out: Unit[] = [];
  const w = ctx.world;
  for (const id of w.unitOrder) {
    const u = w.units[id];
    if (!u || !u.alive || u.faction === src.faction) continue;
    if (range !== undefined && dist(u.pos, src.pos) > range) continue;
    out.push(u);
  }
  return out;
}

/** Seeded partial Fisher–Yates: `n` distinct picks from `list` (order = pick order). */
export function shuffledPick<T>(ctx: SimContext, list: readonly T[], n: number): T[] {
  const a = [...list];
  const k = Math.min(n, a.length);
  for (let i = 0; i < k; i++) {
    const j = ctx.rng.int(i, a.length - 1);
    const t = a[i]!;
    a[i] = a[j]!;
    a[j] = t;
  }
  return a.slice(0, k);
}

/**
 * Pick targets for a mechanic among the source's living enemies.
 * `primaryId` is the effect's target (rule 'primary'); `count` is ignored for 'all'/'allNotTank'.
 */
export function pickTargets(ctx: SimContext, src: Unit, rule: MechanicTargetRule, count: number, primaryId: UnitId | null, range = DEFAULT_PICK_RANGE): Unit[] {
  const pool = hostilesOf(ctx, src, range);
  const victimId = src.threat?.victimId ?? src.targetId;
  const victim = pool.find((u) => u.id === victimId);
  const n = Math.max(0, Math.floor(count));
  const isTank = (u: Unit): boolean => u.role === 'tank';
  switch (rule) {
    case 'primary': {
      const p = pool.find((u) => u.id === primaryId) ?? victim ?? pool[0];
      return p ? [p] : [];
    }
    case 'victim':
      return victim ? [victim] : pool[0] ? [pool[0]] : [];
    case 'all':
      return pool;
    case 'allNotTank': {
      const nt = pool.filter((u) => !isTank(u));
      return nt.length > 0 ? nt : pool;
    }
    case 'random':
      return shuffledPick(ctx, pool, n);
    case 'randomNotTank': {
      const nt = pool.filter((u) => !isTank(u) && u.id !== victim?.id);
      return shuffledPick(ctx, nt.length > 0 ? nt : pool, n);
    }
    case 'nearest':
    case 'farthest': {
      const sign = rule === 'nearest' ? 1 : -1;
      return pool
        .map((u, i) => ({ u, i, d: dist(u.pos, src.pos) }))
        .sort((a, b) => sign * (a.d - b.d) || a.i - b.i)
        .slice(0, n)
        .map((x) => x.u);
    }
    case 'tank': {
      const t = pool.find(isTank) ?? victim;
      return t ? [t] : [];
    }
    case 'healer': {
      const h = pool.find((u) => u.role === 'healer');
      return h ? [h] : shuffledPick(ctx, pool, 1);
    }
  }
}

// ---- effects ------------------------------------------------------------------

/** Run nested effects through combat (keeps the caller's depth; deferred callers pass depth 1). */
export function runEffects(ctx: SimContext, effects: readonly EffectSpec[] | undefined, ectx: EffectCtx): void {
  if (!effects || effects.length === 0) return;
  const { isCast: _drop, ...rest } = ectx;
  ctx.sys.combat.applyEffects(ctx, effects, rest);
}

/** Multiply every magnitude term of an Amount. */
export function scaleAmount(a: Amount, k: number): Amount {
  const out: Amount = { ...a };
  if (a.base !== undefined) out.base = a.base * k;
  if (a.coef !== undefined) out.coef = a.coef * k;
  if (a.pctSourceMaxHp !== undefined) out.pctSourceMaxHp = a.pctSourceMaxHp * k;
  if (a.pctTargetMaxHp !== undefined) out.pctTargetMaxHp = a.pctTargetMaxHp * k;
  if (a.pctRecentDamageTaken !== undefined) out.pctRecentDamageTaken = { ...a.pctRecentDamageTaken, pct: a.pctRecentDamageTaken.pct * k };
  return out;
}

/** Raw (pre-mitigation) magnitude of an Amount from `src` on `t`, keystone-scaled by the source. */
export function amountOf(ctx: SimContext, a: Amount, src: Unit | undefined, t: Unit | undefined): number {
  let v = a.base ?? 0;
  if (a.coef && src) v += a.coef * ctx.sys.combat.effectiveStats(ctx, src.id).power;
  if (a.pctSourceMaxHp && src) v += a.pctSourceMaxHp * src.maxHp;
  if (a.pctTargetMaxHp && t) v += a.pctTargetMaxHp * t.maxHp;
  if (a.variance) v *= 1 + ctx.rng.range(-a.variance, a.variance);
  return Math.max(0, v * (src?.scaling.damage ?? 1));
}

// ---- telegraphs / zones built by mechanics ----------------------------------------

/** Push a mechanic-owned telegraph (emits telegraph_start). `effects` [] = mechanics resolves it itself. */
export function pushTelegraph(
  ctx: SimContext,
  sourceId: UnitId,
  abilityId: string,
  area: WorldShape,
  resolvesAt: Ms,
  opts: { effects?: EffectSpec[]; intent?: HazardIntent; mechanic: MechanicSpec['kind']; soak?: { min: number; inside: number } },
): Telegraph {
  const tg: Telegraph = {
    id: ctx.newId('t'),
    sourceId,
    abilityId,
    area,
    affects: 'enemies',
    createdAt: ctx.now,
    resolvesAt: Math.max(ctx.now + TICK_MS, resolvesAt),
    followUnitId: null,
    effects: opts.effects ?? [],
    repeatsLeft: 0,
    repeatIntervalMs: 0,
    intent: opts.intent ?? 'avoid',
    mechanic: opts.mechanic,
  };
  if (opts.soak) tg.soak = opts.soak;
  ctx.world.telegraphs.push(tg);
  ctx.emit({ type: 'telegraph_start', telegraphId: tg.id, sourceID: sourceId, abilityGameID: abilityId, area, resolvesAt: tg.resolvesAt });
  return tg;
}

/** Remove a telegraph by id, emitting telegraph_end with the given hits. */
export function endTelegraph(ctx: SimContext, id: string, hitIds: UnitId[]): void {
  const w = ctx.world;
  const i = w.telegraphs.findIndex((t) => t.id === id);
  if (i < 0) return;
  const tg = w.telegraphs[i]!;
  w.telegraphs.splice(i, 1);
  ctx.emit({ type: 'telegraph_end', telegraphId: id, sourceID: tg.sourceId, abilityGameID: tg.abilityId, hitIds });
}

/** Push a zone built from a ZoneEffect at an explicit placement (never replaces existing zones). */
export function pushZone(ctx: SimContext, sourceId: UnitId, abilityId: string, e: ZoneEffect, area: WorldShape, intent?: HazardIntent, motion?: ZoneMotionState): Zone {
  const z: Zone = {
    id: ctx.newId('z'),
    defId: e.zoneId,
    sourceId,
    abilityId,
    area: { shape: { ...area.shape }, origin: { x: area.origin.x, y: area.origin.y }, facing: area.facing },
    affects: e.affects,
    createdAt: ctx.now,
    expiresAt: ctx.now + e.durationMs,
    tick: e.tick ? { intervalMs: e.tick.intervalMs, effects: e.tick.effects } : null,
    nextTickAt: e.tick ? ctx.now + e.tick.intervalMs : null,
    insideAuraId: e.insideAura?.id ?? null,
    inside: [],
  };
  if (intent) z.intent = intent;
  if (motion) z.motion = motion;
  ctx.world.zones.push(z);
  return z;
}

/** Arena rect of the encounter `u` belongs to (if any). */
export function arenaOf(ctx: SimContext, u: Unit | undefined): { x: number; y: number; w: number; h: number } | undefined {
  const encId = u?.encounterId ?? ctx.world.encounter?.encounterId;
  return encId ? ctx.content.encounter(encId)?.arena : undefined;
}

/** Encounter markers for `u`'s encounter. */
export function markersOf(ctx: SimContext, u: Unit | undefined, key: string): Vec2[] {
  const encId = u?.encounterId ?? ctx.world.encounter?.encounterId;
  return (encId ? ctx.content.encounter(encId)?.markers?.[key] : undefined) ?? [];
}
